import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import type { Node } from "@xyflow/react";
import { Dialog } from "primereact/dialog";
import { Slider } from "primereact/slider";
import { FlowWheelProps, NodeData } from "../types/types";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { Button } from "primereact/button";

const width = 500;
const height = 500;

const FocusWheel: React.FC<FlowWheelProps> = ({
  name,
  nodes,
  edges,
  onClickNode,
  onUpdateData,
}) => {
  const ref = useRef<SVGSVGElement | null>(null);
  const [tooltipContent, setTooltipContent] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [dialogVisible, setDialogVisible] = useState(false);
  const [zoom, setZoom] = useState<number>(50);

  useEffect(() => {
    // Build hierarchy from nodes using parentId property
    function buildHierarchy(name: string, nodes: Node<NodeData>[]): any {
      const nodeMap: Record<string, any> = {};
      nodes.forEach((n) => (nodeMap[n.id] = { ...n, children: [] }));
      let root = null;
      nodes.forEach((n) => {
        if (n.parentId) {
          if (nodeMap[n.parentId]) {
            nodeMap[n.parentId].children.push(nodeMap[n.id]);
          }
        } else if (n.data.label === name || n.type === "center") {
          root = nodeMap[n.id];
        }
      });
      // fallback: if no node matches name, pick first root
      if (!root) {
        root = nodes.find((n) => !n.parentId)
          ? nodeMap[nodes.find((n) => !n.parentId).id]
          : { data: { label: name }, children: [] };
      }
      return root;
    }

    // Recursively assign x0/x1 so that children of each node fill the parent's arc
    function assignAngles(node: any, x0: number, x1: number) {
      node.x0 = x0;
      node.x1 = x1;
      if (node.children && node.children.length > 0) {
        const n = node.children.length;
        let curX = x0;
        for (let i = 0; i < n; i++) {
          const childArc = (x1 - x0) / n;
          assignAngles(node.children[i], curX, curX + childArc);
          curX += childArc;
        }
      }
    }

    const hierarchyData = buildHierarchy(name, nodes);

    const root = d3
      .hierarchy(hierarchyData)
      .sum((d: Node<NodeData>) => 1)
      .sort((a, b) => (a.data.label > b.data.label ? 1 : -1));

    const radius = Math.min(width, height) / 2;
    // Partition for y0/y1 (radius), but we'll handle x0/x1 (angles) ourselves
    const partition = d3
      .partition<Node<NodeData>>()
      .size([2 * Math.PI, radius]);
    partition(root);

    // Overwrite x0/x1 for all nodes so that children fill parent's arc
    assignAngles(root, 0, 2 * Math.PI);

    const arcGen = d3
      .arc<d3.HierarchyRectangularNode<any>>()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .innerRadius((d) => d.y0)
      .outerRadius((d) => d.y1);

    const svgRoot = d3.select(ref.current);
    svgRoot.selectAll("*").remove();

    const svg = svgRoot
      .attr("viewBox", `0 0 ${width} ${height}`)
      .append("g")
      .attr(
        "transform",
        `translate(${width / 2},${height / 2}) scale(${zoom / 100})`,
      );

    svg
      .selectAll("path")
      .data(root.descendants())
      .join("path")
      .attr("d", arcGen)
      .attr("fill", (d) =>
        d.depth === 0 ? "#fff" : d3.interpolateSpectral(d.depth / 5),
      )
      .attr("stroke", "#fff")
      .on("mousemove", (event, d) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        setTooltipPos({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
        const label = d.data.data.label;
        if (label && label.length > 20) {
          // Insert line break every 20 chars (at word boundary if possible)
          const wrapped = label.replace(/(.{1,20})(\s+|$)/g, "$1\n").trim();
          setTooltipContent(wrapped);
        } else {
          setTooltipContent(label);
        }
      })
      .on("mouseleave", () => {
        setTooltipContent(null);
        setTooltipPos(null);
      })
      .on("dblclick", (event, d) => {
        // Open NotesModal for this node
        if (onClickNode) {
          // Pass the node id or the node object as needed by your NotesModal logic
          onClickNode(d.data);
        }
      });

    // --- Add curved labels to donut layers ---
    svg
      .selectAll("g.sunburst-label-group")
      .data(root.descendants().filter((d) => d.depth > 0))
      .join("g")
      .attr("class", "sunburst-label-group")
      .each(function (d, i) {
        const node = d as d3.HierarchyRectangularNode<any>;
        const angle = (node.x0 + node.x1) / 2;
        const arcSweep = node.x1 - node.x0;
        const r = (node.y0 + node.y1) / 2;
        const isLowerHalf =
          (angle * 180) / Math.PI > 90 && (angle * 180) / Math.PI < 270;

        // Arc path for label
        const arcStart = node.x0;
        const arcEnd = node.x1;
        let x1 = Math.sin(arcStart) * r;
        let y1 = -Math.cos(arcStart) * r;
        let x2 = Math.sin(arcEnd) * r;
        let y2 = -Math.cos(arcEnd) * r;
        let arcFlag = 0;
        let dPath;
        if (isLowerHalf) {
          [x1, y1, x2, y2] = [x2, y2, x1, y1];
          arcFlag = 0;
          dPath = `M${x1},${y1} A${r},${r} 0 ${arcFlag} 0 ${x2},${y2}`;
        } else {
          arcFlag = 0;
          dPath = `M${x1},${y1} A${r},${r} 0 ${arcFlag} 1 ${x2},${y2}`;
        }
        const pathId = `sunburstLabelArc${i}`;

        // Append path to defs
        d3.select(this)
          .append("defs")
          .append("path")
          .attr("id", pathId)
          .attr("d", dPath);

        // Append text on path
        d3.select(this)
          .append("text")
          .attr("class", "sunburst-label")
          .append("textPath")
          .attr("href", `#${pathId}`)
          .attr("startOffset", "50%")
          .attr("text-anchor", "middle")
          .text(d.data.data?.label || d.data.label || "");
      });

    // Determine the label to show in the center
    let centerLabel =
      name && name.trim().length > 0
        ? name
        : (() => {
            const centerNode = nodes.find((n) => n.type === "center");
            return centerNode?.data?.label || "";
          })();

    svg
      .append("text")
      .attr("class", "sunburst-center-label")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .text(centerLabel);
  }, [name, nodes, edges, zoom]);

  return (
    <div className="focuswheel">
      <BsFillInfoCircleFill
        className="focuswheel-info-icon"
        onClick={() => setDialogVisible(true)}
      />
      <Dialog
        header="Hierarki"
        visible={dialogVisible}
        className="focuswheel-help-dialog"
        onHide={() => setDialogVisible(false)}
      >
        <div>
          Dette hjul viser hierarkiet for <b>{name}</b> baseret på data.
        </div>
      </Dialog>

      {(!nodes ||
        nodes.length === 0 ||
        !nodes.some((n) => n.type === "mindmap")) && (
        <div className="focuswheel-empty-overlay">
          <h1 className="focuswheel-empty-title">
            Click the Add button to unfold your goal
          </h1>
        </div>
      )}
      <svg ref={ref} className="focuswheel-svg"></svg>

      <div className="focuswheel-zoom-control">
        <span className="focuswheel-zoom-label">Zoom</span>
        <Slider
          value={zoom}
          min={25}
          max={250}
          step={1}
          onChange={(e) => setZoom(e.value as number)}
          className="focuswheel-zoom-slider"
        />
        <span className="focuswheel-zoom-value">{zoom}%</span>
      </div>
      {tooltipContent && tooltipPos && (
        <div
          style={{
            top: tooltipPos.y,
            left: tooltipPos.x,
          }}
          className="focuswheel-tooltip"
        >
          {tooltipContent.split("\n").map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      )}
      {/* <Button
        icon="pi pi-plus"
        className="p-button-rounded p-button-primary"
        style={{
          position: "fixed",
          bottom: "2rem",
          right: "2rem",
          zIndex: 1000,
          width: "4.5rem",
          height: "3.5rem",
        }}
        onClick={() => alert("Coming soon!")}
        aria-label="Add Event"
      /> */}
    </div>
  );
};

export default FocusWheel;
